import { useEffect } from "react";

/**
 * useSEO — Per-route SEO meta management without a third-party dep.
 *
 * Sets <title>, <meta name="description">, <link rel="canonical">, and
 * Open Graph tags. Reverts to the previous values on unmount so SPA route
 * changes never leave stale tags behind.
 *
 * V23.15.28 — SEO Quick Wins (audit remediation):
 *   - Fixes duplicate <title> across routes
 *   - Fixes duplicate meta descriptions
 *   - Adds canonical link per route (helps HTTPS / www unification)
 */
function setMeta(name, content, attr = "name") {
  if (!content) return null;
  let el = document.head.querySelector(`meta[${attr}="${name}"]`);
  const prev = el ? el.getAttribute("content") : null;
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, name);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
  return { el, prev, created: prev === null };
}

function setLink(rel, href) {
  if (!href) return null;
  let el = document.head.querySelector(`link[rel="${rel}"]`);
  const prev = el ? el.getAttribute("href") : null;
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
  return { el, prev, created: prev === null };
}

export default function useSEO({ title, description, path, image } = {}) {
  useEffect(() => {
    const prevTitle = document.title;
    if (title) document.title = title;

    const descSnap = setMeta("description", description);
    const ogTitleSnap = setMeta("og:title", title, "property");
    const ogDescSnap = setMeta("og:description", description, "property");
    const ogTypeSnap = setMeta("og:type", "website", "property");

    // Canonical
    const base = (process.env.REACT_APP_PUBLIC_URL || window.location.origin).replace(/\/$/, "");
    const canonicalPath = path != null ? path : window.location.pathname;
    const canonicalUrl = `${base}${canonicalPath.startsWith("/") ? "" : "/"}${canonicalPath}`;
    const canonicalSnap = setLink("canonical", canonicalUrl);
    const ogUrlSnap = setMeta("og:url", canonicalUrl, "property");

    let ogImgSnap = null;
    if (image) ogImgSnap = setMeta("og:image", image, "property");

    return () => {
      document.title = prevTitle;
      // Restore prev values (or remove if we created them fresh)
      [descSnap, ogTitleSnap, ogDescSnap, ogTypeSnap, ogUrlSnap, ogImgSnap].forEach((snap) => {
        if (!snap) return;
        if (snap.created) snap.el.remove();
        else snap.el.setAttribute("content", snap.prev || "");
      });
      if (canonicalSnap) {
        if (canonicalSnap.created) canonicalSnap.el.remove();
        else canonicalSnap.el.setAttribute("href", canonicalSnap.prev || "");
      }
    };
  }, [title, description, path, image]);
}
